import React, { useState, useEffect } from 'react';
import SubMenuBar from './SubMenuBar';
import './Settings.css';
import { getItem, setItem, getRecoveryLog, clearRecoveryLog, runStorageHealthCheck, getStorageStats } from '../utils/storage';
import { USER_UUID_KEY } from '../utils/uuidUtils';
import { 
  BAGPIPE_USER_INITIALS_KEY,
  GLOBAL_CHANTER_KEY,
  GLOBAL_MIC_KEY
} from '../utils/constants';
import { 
  runMigrations, 
  needsMigration, 
  getCurrentVersion, 
  getGlobalSettings,
  SCHEMA_VERSION
} from '../utils/migrationPipeline';
import * as Sentry from '@sentry/react';
import { DLG_MICROPHONE_CONNECTION_ERROR, dlg_ShowDialog } from '../utils/CommonDialogs';

// Constants
const STORAGE_RECOVERY_LOG_KEY = 'storage_recovery_log';

// Add utility functions for saving and restoring settings
const exportLocalStorage = () => {
  try {
    const data = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      try {
        // Get the value and preserve its type
        const value = JSON.parse(localStorage.getItem(key));
        data[key] = value;
      } catch (e) {
        console.error(`Error exporting key ${key}:`, e);
        data[key] = null;
      }
    }
    
    // Format as JSON string
    const jsonData = JSON.stringify(data, null, 2);
    
    // Create downloadable file
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = `bagpipe-tutor-settings-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error exporting settings:', error);
    return false;
  }
};

// Add scroll to element function 
const scrollToElement = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

function Settings({ menus = [] }) {
  const [importStatus, setImportStatus] = useState(null);
  const [importError, setImportError] = useState(null);
  const [chanters, setChanters] = useState([]);
  const [selectedChanter, setSelectedChanter] = useState('Standard');
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [userInitials, setUserInitials] = useState(getItem(BAGPIPE_USER_INITIALS_KEY, ''));
  const [schemaInfo, setSchemaInfo] = useState({ 
    currentVersion: 0, 
    needsUpgrade: false 
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [storageHealth, setStorageHealth] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [recoveryLogs, setRecoveryLogs] = useState([]);
  const [showRecoveryLogs, setShowRecoveryLogs] = useState(false);
  
  // New notification state
  const [showNotice, setShowNotice] = useState(true);
  
  // Load chanters and audio devices on component mount
  useEffect(() => {
    // Load chanters from storage
    const savedChanters = getItem('bagpipe_chanters', []);
    setChanters(savedChanters);
    
    // Get audio devices from browser
    const loadAudioDevices = async () => {
      try {
        // Request permissions to ensure we get access
        await navigator.mediaDevices.getUserMedia({ audio: true })
          .then(stream => {
            stream.getTracks().forEach(track => track.stop());
          });
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        setAudioDevices(audioInputs);
        
        // Try to get the previously selected device from localStorage
        // In the future this will be from the global setting
        const battlePrefs = getItem('battle_preferences', {});
        const blasterPrefs = getItem('blaster_preferences', {});
        const chanterTestPrefs = getItem('chantertest_preferences', {});
        
        // Use the first found device or the first device in the list
        const savedDeviceId = battlePrefs.selectedDevice || 
                             blasterPrefs.selectedDevice || 
                             chanterTestPrefs.selectedDevice;
                             
        if (savedDeviceId && audioInputs.some(d => d.deviceId === savedDeviceId)) {
          setSelectedDevice(savedDeviceId);
        } else if (audioInputs.length > 0) {
          setSelectedDevice(audioInputs[0].deviceId);
        }
        
      } catch (error) {
        console.error('Error loading audio devices:', error);
        if (Sentry && Sentry.captureException) Sentry.captureException(error);
        dlg_ShowDialog(DLG_MICROPHONE_CONNECTION_ERROR, { forceShow: true });
      }
    };
    
    loadAudioDevices();
    
    // Try to get the previously selected chanter from localStorage
    const battleChanter = getItem('battle_preferences', {}).selectedChanter;
    const flashcardChanter = getItem('flashcard_preferences', {}).selectedChanter;
    const chanterTestChanter = getItem('chantertest_preferences', {}).selectedChanter;
    
    // Use the first found chanter or 'Standard'
    const savedChanter = battleChanter || flashcardChanter || chanterTestChanter || 'Standard';
    setSelectedChanter(savedChanter);
    
  }, []);
  
  // Load schema version info
  useEffect(() => {
    const currentVersion = getCurrentVersion();
    const needsUpgrade = needsMigration();
    setSchemaInfo({ 
      currentVersion,
      needsUpgrade
    });
  }, []);
  
  // Load recovery logs
  useEffect(() => {
    if (showAdvanced) {
      setRecoveryLogs(getRecoveryLog());
      setStorageStats(getStorageStats());
    }
  }, [showAdvanced]);
  
  const handleChanterChange = (e) => {
    const newChanter = e.target.value;
    setSelectedChanter(newChanter);
    // Immediately update global setting
    setItem(GLOBAL_CHANTER_KEY, newChanter);
  };
  
  const handleDeviceChange = (e) => {
    const newDevice = e.target.value;
    setSelectedDevice(newDevice);
    // Immediately update global setting
    setItem(GLOBAL_MIC_KEY, newDevice);
  };
  
  const handleExportSettings = () => {
    const success = exportLocalStorage();
    if (success) {
      setImportStatus('Settings exported successfully.');
    } else {
      setImportError('Failed to export settings.');
    }
    
    // Clear status after 3 seconds
    setTimeout(() => {
      setImportStatus(null);
      setImportError(null);
    }, 3000);
  };
  
  const handleImportSettings = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setImportError('No file selected.');
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // Store backup of current settings before import (just in case)
        const backup = {};
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          backup[key] = localStorage.getItem(key);
        }
        
        // Clear all localStorage before restoring
        localStorage.clear();
        
        // Apply imported settings
        Object.keys(data).forEach(key => {
          localStorage.setItem(key, JSON.stringify(data[key]));
        });
        
        // Update local state with imported settings
        updateLocalStateFromStorage();
        
        setImportStatus('Settings imported successfully.');
        
        // Clear status after 3 seconds
        setTimeout(() => {
          setImportStatus(null);
          setImportError(null);
        }, 3000);
        
      } catch (error) {
        console.error('Error parsing settings:', error);
        setImportError('Invalid settings file format.');
      }
    };
    
    reader.onerror = (error) => {
      setImportError('Error reading file.');
    };
    
    reader.readAsText(file);
  };
  
  // Helper function to update local component state from localStorage
  const updateLocalStateFromStorage = () => {
    // Try to get schema info
    try {
      const currentVersion = getCurrentVersion();
      const needsUpgrade = needsMigration();
      setSchemaInfo({ 
        currentVersion,
        needsUpgrade
      });
    } catch (error) {
      console.error('Error updating schema info:', error);
    }
    
    // Try to get chanter and device settings
    try {
      // For chanters, check various sources
      const battlePrefs = getItem('battle_preferences', {});
      const flashcardPrefs = getItem('flashcard_preferences', {});
      const chanterTestPrefs = getItem('chantertest_preferences', {});
      
      // Use the first found chanter or 'Standard'
      const savedChanter = getItem(GLOBAL_CHANTER_KEY) || 
                          battlePrefs.selectedChanter || 
                          flashcardPrefs.selectedChanter || 
                          chanterTestPrefs.selectedChanter || 
                          'Standard';
      
      setSelectedChanter(savedChanter);
      
      // For devices, check various sources or get from audio devices
      const battleDevice = getItem('battle_preferences', {}).selectedDevice;
      const blasterDevice = getItem('blaster_preferences', {}).selectedDevice;
      const chanterTestDevice = getItem('chantertest_preferences', {}).selectedDevice;
      
      const savedDevice = getItem(GLOBAL_MIC_KEY) || 
                         battleDevice || 
                         blasterDevice || 
                         chanterTestDevice;
      
      if (savedDevice) {
        setSelectedDevice(savedDevice);
      }
      
      // Re-load chanters
      const savedChanters = getItem('bagpipe_chanters', []);
      setChanters(savedChanters);
    } catch (error) {
      console.error('Error updating local state:', error);
    }
  };

  const handleUpgradeSettings = () => {
    try {
      // Run the migration pipeline
      const results = runMigrations();
      
      if (results === false) {
        setImportStatus('Settings are already at the latest version.');
      } else if (results.error) {
        setImportError(`Migration failed: ${results.error}`);
      } else {
        // Update local state with new global settings
        const globals = getGlobalSettings();
        setSelectedChanter(globals.chanter);
        setSelectedDevice(globals.microphone);
        
        // Show success message with details
        const migrationsRun = Object.keys(results).length;
        setImportStatus(`Successfully upgraded settings (${migrationsRun} migration${migrationsRun === 1 ? '' : 's'} applied).`);
        
        // Update schema info
        setSchemaInfo({
          currentVersion: globals.schemaVersion,
          needsUpgrade: false
        });
      }
      
    } catch (error) {
      console.error('Error during settings upgrade:', error);
      setImportError(`Upgrade failed: ${error.message}`);
    }
    
    // Clear status after 5 seconds
    setTimeout(() => {
      setImportStatus(null);
      setImportError(null);
    }, 5000);
  };

  const handleDeleteSettings = () => {
    // Show confirmation dialog before proceeding
    if (window.confirm('Are you sure you want to delete all settings? This action cannot be undone.')) {
      try {
        // Clear all localStorage
        localStorage.clear();
        
        // Reset state
        setSelectedChanter('Standard');
        setSelectedDevice(null);
        setSchemaInfo({
          currentVersion: 1.0,
          needsUpgrade: true
        });
        
        setImportStatus('All settings have been deleted.');
        
        // Clear status after 5 seconds
        setTimeout(() => {
          setImportStatus(null);
          setImportError(null);
        }, 5000);
      } catch (error) {
        console.error('Error deleting settings:', error);
        setImportError('Failed to delete settings.');
        
        // Clear error after 5 seconds
        setTimeout(() => {
          setImportError(null);
        }, 5000);
      }
    }
  };

  const handleInitialsChange = (e) => {
    const value = e.target.value.toUpperCase();
    setUserInitials(value);
    setItem(BAGPIPE_USER_INITIALS_KEY, value);
  };

  // Function to check storage health
  const checkStorageHealth = () => {
    const healthResult = runStorageHealthCheck();
    setStorageHealth(healthResult);
    
    // Refresh logs after health check
    setRecoveryLogs(getRecoveryLog());
    setStorageStats(getStorageStats());
  };
  
  // Function to clear recovery logs
  const handleClearRecoveryLogs = () => {
    clearRecoveryLog();
    setRecoveryLogs([]);
  };

  // Add the simulateStorageCorruption function inside the component
  // so it can access the state variables
  const simulateStorageCorruption = () => {
    try {
      // Corrupt the chanters data with invalid JSON
      localStorage.setItem('bagpipe_chanters', '[{"id":12345,"name":"Test Chanter","frequencies":{'); // Invalid JSON
      
      // Add a testing log
      const logs = getItem(STORAGE_RECOVERY_LOG_KEY, []);
      logs.push({
        timestamp: new Date().toISOString(),
        message: 'TEST: Simulated corruption of bagpipe_chanters'
      });
      setItem(STORAGE_RECOVERY_LOG_KEY, logs);
      
      setImportStatus('Storage corrupted for testing. Run the health check to see results.');
      
      // Update logs display if showing
      if (showAdvanced && showRecoveryLogs) {
        setRecoveryLogs(getItem(STORAGE_RECOVERY_LOG_KEY, []));
      }
      
      setTimeout(() => {
        setImportStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error simulating corruption:', error);
      setImportError('Failed to simulate corruption.');
      
      setTimeout(() => {
        setImportError(null);
      }, 3000);
    }
  };

  // Add a manual storage repair function
  const handleRepairStorage = () => {
    try {
      // Run a health check first
      const healthResult = runStorageHealthCheck();
      
      if (healthResult.valid) {
        setImportStatus('Storage is already healthy, no repairs needed.');
        setTimeout(() => setImportStatus(null), 3000);
        return;
      }
      
      // Get the corrupted keys
      const corruptedKeys = healthResult.corruptKeys || [];
      
      // Log the repair attempt
      const logs = getItem(STORAGE_RECOVERY_LOG_KEY, []);
      logs.push({
        timestamp: new Date().toISOString(),
        message: `Manual repair initiated for ${corruptedKeys.length} corrupted keys`
      });
      setItem(STORAGE_RECOVERY_LOG_KEY, logs);
      
      // For each corrupted key, try to restore default or remove it
      let repairedCount = 0;
      
      corruptedKeys.forEach(entry => {
        const { key } = entry;
        
        // Handle known keys with default values
        if (key === 'bagpipe_chanters') {
          // Reset chanters to empty array
          setItem('bagpipe_chanters', []);
          repairedCount++;
        } 
        else if (key === 'global_selected_chanter') {
          // Reset to Standard chanter
          setItem('global_selected_chanter', 'Standard');
          repairedCount++;
        }
        else if (key === 'global_selected_microphone') {
          // Reset to null (app will find default microphone)
          setItem('global_selected_microphone', null);
          repairedCount++;
        } 
        else {
          // For unknown keys, just remove them
          try {
            localStorage.removeItem(key);
            repairedCount++;
          } catch (e) {
            console.error(`Failed to remove corrupted key ${key}:`, e);
          }
        }
      });
      
      // Update logs with results
      logs.push({
        timestamp: new Date().toISOString(),
        message: `Manual repair completed: ${repairedCount}/${corruptedKeys.length} keys fixed`
      });
      setItem(STORAGE_RECOVERY_LOG_KEY, logs);
      
      // Refresh logs display
      setRecoveryLogs(getItem(STORAGE_RECOVERY_LOG_KEY, []));
      
      // Run another check to verify repairs
      const postRepairCheck = runStorageHealthCheck();
      setStorageHealth(postRepairCheck);
      
      // Display success message
      if (postRepairCheck.valid) {
        setImportStatus(`Successfully repaired ${repairedCount} corrupted storage items.`);
      } else {
        setImportError(`Repair partially successful. ${postRepairCheck.corruptKeys.length} items still corrupted.`);
      }
      
      // Clear status after 5 seconds
      setTimeout(() => {
        setImportStatus(null);
        setImportError(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error repairing storage:', error);
      setImportError('Failed to repair storage: ' + error.message);
      setTimeout(() => setImportError(null), 5000);
    }
  };

  return (
    <div className="settings">
      <SubMenuBar title="Settings" menus={menus} />
      
      {/* Add floating notice about advanced settings */}
      {showNotice && (
        <div className="floating-notice">
          <div className="notice-content">
            <p>Having app issues? Try the <strong>Advanced Settings</strong> at the bottom of this page.</p>
            <div className="notice-actions">
              <button 
                className="notice-button"
                onClick={() => scrollToElement('advanced-settings-section')}
              >
                Go to Advanced Settings
              </button>
              <button 
                className="close-notice"
                onClick={() => setShowNotice(false)}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="settings-container">
        <div className="settings-content">
          <h1>Settings</h1>
          
          <section className="settings-section">
            <h2>User</h2>
            <div className="settings-input-group">
              <label htmlFor="user-id">User ID:</label>
              <span className="settings-text">{getItem(USER_UUID_KEY, '')}</span>
            </div>
            <div className="settings-input-group">
              <label htmlFor="user-initials">Initials:</label>
              <input
                id="user-initials"
                type="text"
                maxLength={3}
                value={userInitials}
                onChange={handleInitialsChange}
                className="settings-input"
                placeholder="Enter 3 initials"
              />
            </div>
          </section>
          
          <section className="settings-section">
            <div className="settings-input-group">
              <label htmlFor="microphone-select">Microphone:</label>
              <select
                id="microphone-select"
                value={selectedDevice || ''}
                onChange={handleDeviceChange}
                className="settings-select"
              >
                {audioDevices.length === 0 ? (
                  <option value="">No devices found</option>
                ) : (
                  audioDevices.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId.slice(0, 5)}...`}
                    </option>
                  ))
                )}
              </select>
            </div>
          </section>
          
          <section className="settings-section">
            <div className="settings-input-group">
              <label htmlFor="chanter-select">Chanter:</label>
              <select
                id="chanter-select"
                value={selectedChanter}
                onChange={handleChanterChange}
                className="settings-select"
              >
                <option value="Standard">Standard</option>
                <option value="Keyboard">Keyboard</option>
                {chanters.map(chanter => (
                  <option key={chanter.id} value={chanter.name}>
                    {chanter.name}
                  </option>
                ))}
              </select>
            </div>
          </section>
          
          <section className="settings-section">
            <h2>Backup & Restore</h2>
            <p>
              Export your current settings to a file or restore from a previously saved settings file.
            </p>
            
            <div className="settings-buttons-container">
              <button 
                className="settings-button" 
                onClick={handleExportSettings}
              >
                Backup Settings
              </button>
              
              <div className="settings-button-spacer"></div>
              
              <label className="settings-button" htmlFor="settingsFile">
                Restore Settings
              </label>
              <input 
                type="file" 
                id="settingsFile" 
                accept=".json" 
                style={{ display: 'none' }}
                onChange={handleImportSettings}
              />
              
              <div className="settings-button-spacer"></div>
              
              <button 
                className="settings-button settings-button-danger" 
                onClick={handleDeleteSettings}
              >
                Delete Settings
              </button>
            </div>
            
            <div className="settings-upgrade-container">
              <button 
                className="settings-button settings-upgrade-button"
                onClick={handleUpgradeSettings}
                disabled={!schemaInfo.needsUpgrade}
              >
                Upgrade Settings
                {schemaInfo.needsUpgrade && <span className="settings-badge">!</span>}
              </button>
              <div className="settings-version-info">
                Current version: {schemaInfo.currentVersion.toFixed(1)} 
                {schemaInfo.needsUpgrade ? 
                  ` (Upgrade to ${SCHEMA_VERSION.toFixed(1)} available)` : 
                  ' (Latest)'}
              </div>
            </div>
            
            {importStatus && <div className="status-message success">{importStatus}</div>}
            {importError && <div className="status-message error">{importError}</div>}
          </section>
          
          <section className="settings-section" id="advanced-settings-section">
            <h2>Advanced Settings</h2>
            <p className="settings-description">
              These settings are for troubleshooting and diagnostics. You can check your storage health
              and view recovery logs to help identify and fix issues with the app.
            </p>
            
            <button 
              className="toggle-advanced-button"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 
                <span>▲ Hide Advanced Settings</span> : 
                <span>▼ Show Advanced Settings <small style={{display: 'block', marginTop: '5px'}}>Diagnostics & Troubleshooting</small></span>
              }
            </button>
            
            {showAdvanced && (
              <div className="advanced-settings">
                <div className="storage-section">
                  <h4>Storage Health</h4>
                  <p>Check the health of your app's storage and fix issues if needed.</p>
                  
                  <div className="storage-actions">
                    <button className="check-storage-button" onClick={checkStorageHealth}>
                      Check Storage Health
                    </button>
                    <button className="show-logs-button" onClick={() => setShowRecoveryLogs(!showRecoveryLogs)}>
                      {showRecoveryLogs ? 'Hide Recovery Logs' : 'Show Recovery Logs'}
                    </button>
                    {recoveryLogs.length > 0 && (
                      <button className="clear-logs-button" onClick={handleClearRecoveryLogs}>
                        Clear Recovery Logs
                      </button>
                    )}
                  </div>
                  
                  {/* Add the test button in development mode only */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="test-section">
                      <h5>Testing Tools</h5>
                      <p>These buttons are for testing the storage recovery system. Only available in development mode.</p>
                      <div className="test-actions">
                        <button 
                          className="test-corruption-button" 
                          onClick={simulateStorageCorruption}
                          title="Corrupts the chanters storage with invalid JSON"
                        >
                          Simulate Storage Corruption
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {storageHealth && (
                    <div className="storage-health-results">
                      <h5>Storage Health Check Results</h5>
                      <div className="health-status">
                        <span className={`status-indicator ${storageHealth.valid ? 'healthy' : 'unhealthy'}`}>
                          {storageHealth.valid ? '✅ Healthy' : '⚠️ Issues Detected'}
                        </span>
                        <div className="health-details">
                          <p>Checked: {storageHealth.timestamp}</p>
                          <p>Total Keys: {storageHealth.totalKeys}</p>
                          {!storageHealth.valid && (
                            <div className="corrupt-keys">
                              <p>Corrupt Keys: {storageHealth.corruptKeys.length}</p>
                              <ul>
                                {storageHealth.corruptKeys.map((entry, index) => (
                                  <li key={index}>
                                    "{entry.key}": {entry.error}
                                  </li>
                                ))}
                              </ul>
                              <div className="repair-actions">
                                <button 
                                  className="repair-button"
                                  onClick={handleRepairStorage}
                                  title="Attempt to repair corrupted storage items"
                                >
                                  🔧 Repair Storage
                                </button>
                                <p className="repair-note">
                                  This will attempt to fix corrupted storage by resetting affected items to defaults.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {storageStats && (
                    <div className="storage-stats">
                      <h5>Storage Usage</h5>
                      <p>Total Space Used: {Math.round(storageStats.totalSize / 1024)} KB ({storageStats.percentUsed})</p>
                      <p>Total Keys: {storageStats.totalKeys}</p>
                      
                      {storageStats.largestKeys.length > 0 && (
                        <div className="largest-keys">
                          <h6>Largest Keys:</h6>
                          <ul>
                            {storageStats.largestKeys.map((entry, index) => (
                              <li key={index}>
                                "{entry.key}": {Math.round(entry.size / 1024)} KB
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {showRecoveryLogs && (
                    <div className="recovery-logs">
                      <h5>Recovery Logs</h5>
                      {recoveryLogs.length === 0 ? (
                        <p>No recovery events recorded.</p>
                      ) : (
                        <div className="logs-container">
                          <table className="logs-table">
                            <thead>
                              <tr>
                                <th>Timestamp</th>
                                <th>Event</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recoveryLogs.map((log, index) => (
                                <tr key={index}>
                                  <td className="log-timestamp">{new Date(log.timestamp).toLocaleString()}</td>
                                  <td className="log-message">{log.message}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default Settings; 